/**
 * Interaction Preload Strategy
 *
 * Preloads on first user interaction (mousemove or scroll).
 * This is a good balance between immediate preloading (bad for Lighthouse)
 * and waiting too long (user might trigger popup before preload).
 */

import type { PreloadStrategy } from './types';
import { createLogger } from '../../shared/logger';

const logger = createLogger('InteractionPreload');

export type InteractionType = 'mousemove' | 'scroll' | 'any';

export class InteractionPreloadStrategy implements PreloadStrategy {
	private interactionType: InteractionType;
	private hasTriggered = false;
	private boundHandler: () => void;

	/**
	 * @param interactionType Which interaction to listen for ('mousemove', 'scroll', or 'any')
	 */
	constructor(interactionType: InteractionType = 'any') {
		logger.log('constructor - interactionType:', interactionType);
		this.interactionType = interactionType;
		this.boundHandler = () => {}; // Will be set in init
	}

	/**
	 * Start listening for interactions
	 */
	public init(onPreload: () => void): void {
		logger.log('init() called, interactionType:', this.interactionType);

		this.boundHandler = () => {
			if (this.hasTriggered) {
				return;
			}
			logger.log('Interaction detected! Triggering preload');
			this.hasTriggered = true;
			this.removeListeners();
			onPreload();
		};

		this.addListeners();
	}

	/**
	 * Stop listening
	 */
	public destroy(): void {
		logger.log('destroy() called');
		this.removeListeners();
	}

	private addListeners(): void {
		const options = { once: true, passive: true };

		if (
			this.interactionType === 'mousemove' ||
			this.interactionType === 'any'
		) {
			document.addEventListener('mousemove', this.boundHandler, options);
		}

		if (
			this.interactionType === 'scroll' ||
			this.interactionType === 'any'
		) {
			window.addEventListener('scroll', this.boundHandler, options);
		}

		// Also listen for touch on mobile
		if (this.interactionType === 'any') {
			document.addEventListener('touchstart', this.boundHandler, options);
		}
	}

	private removeListeners(): void {
		document.removeEventListener('mousemove', this.boundHandler);
		window.removeEventListener('scroll', this.boundHandler);
		document.removeEventListener('touchstart', this.boundHandler);
	}
}
